import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import Input from "../../components/Form/Input/input";
import { FormGroup } from "../../components/Form/styles";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { currenciesIn } from "../Editor/pages/Pricing/components/CurrencySelect";
import { LangOptions } from "./style";

const prefix = "pages.account.actions.currency";

const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

type Props = {
  current: string;
  saving: boolean;
  error?: string;
  onSelect: (code: string) => void;
  onExit: () => void;
};

/**
 * The account's currency: a search and the list (as the Pricing section's: Real, Dollar and Euro first), one marked;
 * it's only saved on "Selecionar", as the language.
 */
export const CurrencyModal = ({ current, saving, error, onSelect, onExit }: Props) => {
  const { t, i18n } = useTranslation();
  const [picked, setPicked] = useState(current);
  const [query, setQuery] = useState("");
  const all = useMemo(() => currenciesIn(i18n.language), [i18n.language]);
  const shown = useMemo(() => {
    const words = normalize(query.trim());
    return words ? all.filter(({ code, name, symbol }) => normalize(`${code} ${name} ${symbol}`).includes(words)) : all;
  }, [all, query]);

  return (
    <Modal
      outsideExit
      busy={saving}
      error={error}
      onExit={onExit}
      title={t(`${prefix}.modalTitle`)}
      subtitle={t(`${prefix}.modalSubtitle`)}
      footer={
        <>
          <ModalSecondaryButton type="button" onClick={onExit}>
            {t("actions.cancel")}
          </ModalSecondaryButton>
          <ModalButton loading={saving} disabled={picked === current} onClick={() => onSelect(picked)}>
            {t(`${prefix}.select`)}
          </ModalButton>
        </>
      }>
      <FormGroup>
        <Input
          id="currency-search"
          type="search"
          label={t(`${prefix}.search`)}
          value={query}
          ignoreLengthInfo
          onChange={event => setQuery(event.target.value)}
        />
      </FormGroup>
      {shown.length === 0 ? (
        <Empty>{t(`${prefix}.none`)}</Empty>
      ) : (
        <Options role="radiogroup" aria-label={t(`${prefix}.modalTitle`)}>
          {shown.map(({ code, name, symbol }) => {
            const isPicked = code === picked;
            return (
              <li key={code}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isPicked}
                  className={isPicked ? "selected" : undefined}
                  onClick={() => setPicked(code)}>
                  <span className="symbol" aria-hidden="true">
                    {symbol}
                  </span>
                  <span className="text">
                    <b>{name}</b>
                    <span>{code}</span>
                  </span>
                  {code === current && <span className="current">{t(`${prefix}.current`)}</span>}
                  <span className="radio" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </Options>
      )}
    </Modal>
  );
};

/** The language's list, with the currency's symbol in the flag's square. */
const Options = styled(LangOptions)`
  /* only the list scrolls: the search stays in sight above it */
  max-height: min(22rem, 50vh);
  padding: 2px 4px 2px 0;
  overflow-y: auto;
  overscroll-behavior: contain;

  /* the name and its code close together */
  .text {
    gap: 0;
  }

  .text b {
    line-height: 1.25rem;
  }

  .text span {
    line-height: 1rem;
  }

  .symbol {
    display: grid;
    flex-shrink: 0;
    place-content: center;
    width: 40px;
    height: 30px;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 10px;
  }
`;

const Empty = styled.p`
  padding: 1rem 0;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  color: var(--color-neutral-150);
  text-align: center;
`;
